/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { Close } from '@carbon/react/icons';
import { Button } from 'carbon-components-react';
import cx from 'classnames';
import React, {
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
  ReactNode
} from 'react';
import ReactDOM from 'react-dom';

import { Noop } from '../../../../ts';

import './Panel.scss';

const PANEL_ANIMATION = 'Panel--animation';
const PANEL_SCROLL_UP_THRESHOLD_DEFAULT = 50;
const PANEL_SCROLL_DOWN_THRESHOLD_DEFAULT = 100;

const FOCUSABLE_ELEMENTS_SELECTOR =
  'button:not([disabled]):not(.bx--tab--overflow-nav-button--hidden), [href]:not([disabled]), input:not([disabled]):not([tabindex="-1"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"]):not([disabled])';

export const scrollPanelToTop = () => {
  const domNode = document.querySelector(`#${PANEL_ANIMATION}`);
  domNode.scrollTop = 0;
};

export interface PanelProps {
  additionalFocusableElts?: string[];
  children: ReactNode;
  useCustomParentRoot?: boolean;
  focusOnElement?: Noop | string;
  panelRoot?: string;
  showTitle?: boolean;
  title: string;
  className?: string;
  contentClassName?: string;
  contentId?: string;
  onClose?: Noop;
  hasNavigation?: boolean;
  scrollUpThreshold?: number;
  scrollDownThreshold?: number;
  light?: boolean;
  showTemplateHeader?: boolean;
  toggleTemplateSidePanel?: Noop;
  isOpenTemplateSidePanel?: boolean;
  renderTemplateCartIcon?: Noop;
}
const Panel: React.FC<PanelProps> = ({
  scrollUpThreshold,
  scrollDownThreshold,
  focusOnElement,
  additionalFocusableElts,
  children,
  className,
  onClose,
  contentClassName,
  contentId,
  showTitle,
  title,
  useCustomParentRoot,
  panelRoot,
  hasNavigation,
  light,
  showTemplateHeader,
  toggleTemplateSidePanel,
  isOpenTemplateSidePanel,
  renderTemplateCartIcon
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [mount, setMount] = useState(false);
  const panelRef = useRef<HTMLDivElement>();
  const closeButton = useRef();
  const scrollRef = useRef<HTMLDivElement>();

  // Get all the focusable elements in the panel
  const getFocusableElementsFuncRef = useRef(null);
  getFocusableElementsFuncRef.current = () => {
    let allVisibleFocusableElements = [];
    const panel = panelRef.current;
    if (panel) {
      allVisibleFocusableElements = Array.from(
        panel.querySelectorAll(FOCUSABLE_ELEMENTS_SELECTOR)
      );
    }

    if (additionalFocusableElts) {
      allVisibleFocusableElements.push(...additionalFocusableElts);
    }
    return allVisibleFocusableElements.filter(element => {
      const style = window.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden') {
        return false;
      }
      return !!(
        element.offsetWidth ||
        element.offsetHeight ||
        element.getClientRects().length
      );
    });
  };

  // Handler to trap focus (ensure focus can't leave the panel)
  const onKeyDown = useCallback(event => {
    // Check if tab was pressed
    const isTabPressed = event.key === 'Tab' || event.keyCode === 9;
    if (!isTabPressed) {
      return;
    }

    // Find all visible focusable elements in the panel
    const allVisibleFocusableElements = getFocusableElementsFuncRef.current();
    const firstFocusableElement = allVisibleFocusableElements[0];
    const lastFocusableElement =
      allVisibleFocusableElements.length > 0
        ? allVisibleFocusableElements[allVisibleFocusableElements.length - 1]
        : firstFocusableElement;

    if (event.shiftKey) {
      // Shift-Tab, so focus last focusable element if on the first
      if (document.activeElement === firstFocusableElement) {
        if (lastFocusableElement) {
          lastFocusableElement.focus();
        }
        event.preventDefault();
      }
    } else {
      // Tab, so focus on the first focusable elements if on the last
      if (document.activeElement === lastFocusableElement) {
        if (firstFocusableElement) {
          firstFocusableElement.focus();
        }
        event.preventDefault();
      }
    }
  }, []);

  const onScroll = useCallback(() => {
    if (!scrolled && scrollRef.current.scrollTop > scrollDownThreshold) {
      // Panel has scrolled down enough so set scrolled state
      setScrolled(true);
    } else if (scrolled && scrollRef.current.scrollTop < scrollUpThreshold) {
      // Panel has scrolled back up enough so unset scrolled state
      setScrolled(false);
    }
  }, [scrollDownThreshold, scrollUpThreshold, scrolled]);

  useLayoutEffect(() => {
    document.body.classList.add('Panel--opened');
    document.body.scrollTop = document.documentElement.scrollTop = 0;
    setMount(true);
    return () => {
      document.body.classList.remove('Panel--opened');
    };
  }, []);

  useLayoutEffect(() => {
    const scrollElement = scrollRef.current;
    scrollElement.addEventListener('scroll', onScroll);
    return () => {
      scrollElement.removeEventListener('scroll', onScroll);
    };
  }, [onScroll]);

  useLayoutEffect(() => {
    // Ensure we pull keyboard focus into the panel
    let focusSetByChild = false;

    if (focusOnElement) {
      if (typeof focusOnElement === 'string') {
        const element = document.getElementById(focusOnElement);
        if (element && element.focus) {
          setTimeout(() => element.focus(), 500);
          focusSetByChild = true;
        }
      } else if (typeof focusOnElement === 'function') {
        focusOnElement();
        focusSetByChild = true;
      }
    }
    if (!focusSetByChild) {
      setTimeout(() => {
        const allVisibleFocusableElements =
          getFocusableElementsFuncRef.current();
        let firstFocusableElement = allVisibleFocusableElements[0];
        if (firstFocusableElement) {
          if (
            firstFocusableElement.id === 'closePanel' &&
            allVisibleFocusableElements.length > 1
          ) {
            firstFocusableElement = allVisibleFocusableElements[1];
          }
          firstFocusableElement.focus();
        }
      }, 100);
    }
    document.addEventListener('keydown', onKeyDown, { capture: true });
    return () => {
      document.removeEventListener('keydown', onKeyDown, { capture: true });
    };
  }, [focusOnElement, onKeyDown]);

  // IMPORTANT when setting useCustomParentRoot to true, you MUST
  // define a dom node with the id 'custom-panel-root'
  const parentId = panelRoot
    ? panelRoot
    : useCustomParentRoot
    ? 'custom-panel-root'
    : 'panel-root';
  let domNode = document.querySelector(`#${parentId}`);

  // Adds the div if it doesn't exist (useful for testing)
  if (!domNode) {
    domNode = document.createElement('div');
    domNode.setAttribute('id', parentId);
    document.body.appendChild(domNode);

    console.warn(
      `Panel was unable to find "${parentId}" DOM node to add portal: created DOM node!`
    );
  }

  if (hasNavigation) {
    domNode?.classList.add('Panel--navigation');
  } else {
    domNode?.classList.remove('Panel--navigation');
  }

  return ReactDOM.createPortal(
    <div
      id={PANEL_ANIMATION}
      ref={panelRef}
      role="dialog"
      aria-label={title}
      className={cx(
        'Panel--showing',
        { 'Panel--closed': !mount, 'Panel--light': light },
        className
      )}>
      {showTemplateHeader ? (
        <div
          className={cx('Panel__header', {
            'Panel__header--templates': showTemplateHeader
          })}>
          {showTitle && (
            <span className="Panel__title--templates">{title}</span>
          )}
          <Button
            className={
              isOpenTemplateSidePanel
                ? 'Panel__button--open-templates-side-panel'
                : 'Panel__button--close-templates-side-panel'
            }
            kind="ghost"
            renderIcon={renderTemplateCartIcon}
            iconDescription="Side panel"
            hasIconOnly
            onClick={() => toggleTemplateSidePanel()}
          />
          {onClose && !hasNavigation && (
            <button
              className="Panel__button--close-templates-catalog bx--modal-close"
              id="closePanel"
              aria-label="Close panel"
              title="Close panel"
              onClick={() => onClose()}
              ref={closeButton}
              tabIndex={0}>
              <Close
                size={32}
                className="Panel__button--close-templates-catalog__icon bx--modal-close__icon"
              />
            </button>
          )}
        </div>
      ) : (
        <div
          className={cx('Panel__header', {
            'Panel__header--with-title': showTitle
          })}>
          {showTitle && <span className="Panel__title--optional">{title}</span>}
          {onClose && !hasNavigation && (
            <button
              className="Panel__button--close bx--modal-close"
              id="closePanel"
              aria-label="Close panel"
              title="Close panel"
              onClick={() => onClose()}
              ref={closeButton}
              tabIndex={0}>
              <Close size={32} className="bx--modal-close__icon" />
            </button>
          )}
        </div>
      )}
      <div
        ref={scrollRef}
        id={contentId}
        className={cx('Panel__content', contentClassName, {
          'Panel__content--scrolled': scrolled
        })}
        tabIndex={-1}>
        {children}
      </div>
    </div>,
    domNode
  );
};

Panel.defaultProps = {
  contentId: 'Panel__content',
  scrollUpThreshold: PANEL_SCROLL_UP_THRESHOLD_DEFAULT,
  scrollDownThreshold: PANEL_SCROLL_DOWN_THRESHOLD_DEFAULT
};

export default Panel;
